import { NxIconModule } from "@allianz/ng-aquila/icon";
import { NxSpinnerModule } from "@allianz/ng-aquila/spinner";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { Store } from "@ngxs/store";
import { AuthSessionFacade } from "@services/auth-session/auth-session.facade";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import {
  ACTIONS,
  NOTIFICATION_MESSAGE_CATEGORY,
} from "@shared/enums/index.enum";
import { GET_REDIRECT_URL } from "@store/customer-management/customer-management.action";
import {
  GET_NOTIFICATION_LIST,
  RESET_NOTIFICATION_LIST,
  SEND_READ_NOTIFICATION,
} from "@store/notification/notification.action";
import { NotificationState } from "@store/notification/notification.state";
import { InfiniteScrollDirective } from "ngx-infinite-scroll";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

interface NotificationListCursor {
  page: number;
  size: number;
  dateFrom?: string;
}

@Component({
  selector: "app-notification-sidebar",
  imports: [
    CommonModule,
    NxIconModule,
    NxSpinnerModule,
    InfiniteScrollDirective,
    TranslateModule,
  ],
  templateUrl: "./notification-sidebar.component.html",
  styleUrls: ["./notification-sidebar.component.scss"],
})
export class NotificationSidebarComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output() public toggledNotification = new EventEmitter<boolean>();

  protected unreadNotification$ = this.store.select(
    NotificationState.countUnreadNotification,
  );

  private asOfDate: string;
  public isLoading: boolean = true;
  private notificationFlag: boolean;
  private totalPages: number = 0;
  private userId: string;

  protected messages: any = [];
  private data: NotificationListCursor = {
    page: 0,
    size: 10,
  };
  public throttle = 680;
  public scrollDistance = 2;
  private readonly direction = "";
  public isEmptyNotifiList: boolean = false;
  public scrolledDown: boolean = false;

  constructor(
    private readonly authSessionFacade: AuthSessionFacade,
    private readonly elementRef: ElementRef,
    private readonly store: Store,
    private readonly crossAppNavigationService: CrossAppNavigationService,
    private readonly cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.userId = this.authSessionFacade.userSnapshot?.userId ?? "";
    if (!this.userId) {
      this.authSessionFacade.user$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((user) => {
          if (!this.userId && user?.userId) {
            this.userId = user.userId;
            this.getNotificationList(this.data);
          }
        });
    } else {
      this.getNotificationList(this.data);
    }

    this.store
      .select(NotificationState.getNotificationList)
      .pipe(
        map((list: any) => list?.messages ?? []),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((messages: any[]) => {
        this.messages = messages;
        this.cdRef.markForCheck();
      });
  }

  private getNotificationList(data: NotificationListCursor) {
    if (!this.userId) {
      return;
    }

    this.notificationFlag = true;
    const payload = { ...data, userId: this.userId };
    this.store
      .dispatch(new GET_NOTIFICATION_LIST(payload, true))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: any) => {
        const notificationList = state.NotificationState.notificationList;
        if (notificationList?.messages?.length > 0) {
          this.asOfDate = notificationList.asOfDate;
          this.totalPages = notificationList.totalPages;
          this.isLoading = this.totalPages <= 1 ? false : true;
          this.notificationFlag = false;
        } else {
          this.isLoading = false;
          this.notificationFlag = true;
          this.isEmptyNotifiList = true;
        }
        this.cdRef.markForCheck();
      });
  }

  protected notifClicked(notification: any) {
    const payload = {
      id: notification.id,
      seenInd: true,
      userId: this.userId,
    };

    this.store
      .dispatch(new SEND_READ_NOTIFICATION(payload))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (
          notification.messageCategory ===
          NOTIFICATION_MESSAGE_CATEGORY.MOTOR_AUTO_REN_QUOTATION
        ) {
          this.store
            .dispatch(
              new GET_REDIRECT_URL({
                operator: this.userId,
                action: ACTIONS.SUMMARY,
              }),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        } else if (this.isInternalLink(notification.link)) {
          this.crossAppNavigationService.redirect(notification.link, "_self");
        } else {
          this.crossAppNavigationService.redirect(notification.link, "_blank");
        }
      });
  }

  /**
   * Internal (same-tab) links are those pointing back into our own app
   * family. The BFF builds notification links per environment: alpha-sat
   * deployments emit "https://<alpha-host>/alpha-sat/..." (detected by the
   * path segment, as the source alpha-sat repo did), while sat deployments
   * emit links on the sat host itself with no path prefix
   * (e.g. "https://sat-dev.allianz.com.my/e-library/..."), so those are
   * detected by host — either the host serving this app or the host of the
   * configured backend (which keeps local dev behaving like the deployment
   * it points at).
   */
  private isInternalLink(link: string): boolean {
    if (!link) {
      return false;
    }

    if (link.split("/")[3] === "alpha-sat") {
      return true;
    }

    try {
      const linkHost = new URL(link).host;
      return (
        linkHost === globalThis.location.host ||
        linkHost === new URL(environment.apiUrl).host
      );
    } catch {
      return false;
    }
  }

  protected getClass(seen: any) {
    if (seen) {
      return "notification-read";
    } else {
      return "notification";
    }
  }

  protected getIcon(notification: any) {
    if (notification.linkType === "DOC") {
      if (notification.seenInd) {
        return "download-active";
      } else {
        return "download-inactive";
      }
    } else if (
      notification.messageCategory === "CLAIMS_MSG" ||
      notification.messageCategory === "RENEWAL_MSG" ||
      notification.messageCategory === "POLICY_MSG"
    ) {
      return "icon-status";
    } else {
      return "icon-notif";
    }
  }

  protected toCamelCase(string: string): string {
    let formattedString = "";
    const stringArr = string.split(" ");
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < stringArr.length; i++) {
      if (i > 0) {
        formattedString = formattedString.concat(" ");
      }
      formattedString = formattedString.concat(
        stringArr[i].charAt(0).toUpperCase() +
          stringArr[i].substr(1).toLowerCase(),
      );
    }
    return formattedString;
  }

  @HostListener("document:click", ["$event"])
  protected clickout(event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.toggledNotification.emit(false);
      this.elementRef.nativeElement.ownerDocument.body.style.position = null;
      this.elementRef.nativeElement.ownerDocument.body.style.overflow = null;
    }
  }

  protected onScrollDown(_ev) {
    this.scrolledDown = true;
    if (!this.notificationFlag) {
      this.data = {
        page: this.data.page + 1,
        size: this.data.size,
      };
      if (this.data.page < this.totalPages) {
        this.data.dateFrom = this.asOfDate;
        this.getNotificationList(this.data);
      } else {
        this.isLoading = false;
      }
    }
  }

  ngOnDestroy() {
    this.store.dispatch(new RESET_NOTIFICATION_LIST());
    // ensure any scroll-lock is removed if the component is destroyed
    try {
      this.elementRef.nativeElement.ownerDocument.body.style.overflow = null;
      this.elementRef.nativeElement.ownerDocument.body.style.position = null;
    } catch (_error) {
      // ignore
    }
  }
}
